import type {
  CellRange,
  CellRangeParams,
  ColDef,
  ColGroupDef,
  Column,
  ColumnMovedEvent,
  ColumnState,
  DragStoppedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RangeSelectionChangedEvent,
  RowHeightParams,
} from "ag-grid-community";
import { type IRowNode } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { debounce } from "lodash-es";
import { symmetricDifference } from "ramda";
import { type JSX, PureComponent, type ReactElement } from "react";
import { formatDateTimeFromISO, LoadingSpinner, themeSelector } from "widgets";

import {
  type Attribute,
  type InputTableQuerySpec,
  type SearchAttribute,
  type SortData,
  type Table,
} from "@/graphql.ts";
import {
  createUniqueKeyFromTableSpec,
  getActiveExpansionPath,
  getSortOrderExpressionFromTableColumns,
  type IIdentifiable,
  type ITableColumn,
  type ITableData,
  sameTableQuerySpecRestrictions,
} from "@/redux/modules/table-data.ts";
import { CELL_TYPE, DEFAULT_MIN_COLUMN_WIDTH, HEADER_GROUP_HEIGHT, ROW_HEIGHT } from "@/settings/settings.ts";
import { CHECKBOX_COLUMN, MAX_ROW_COUNT } from "@/ui-modules/core/grid/settings.ts";

import { getFilterExpression } from "../../../app-utils/filter.js";
import {
  calculateRowHeightForExpandedColumns,
  createColumnGroups,
  preventColumnPinning,
} from "../../../app-utils/grid.js";
import { type RsqlFilterConstraint } from "../../../app-utils/rsql.js";
import {
  cloneSelectionModel,
  emptySelectionModel,
  type ISelectionModel,
  isSelected,
  setSelected,
} from "../../../app-utils/selection-model.js";
import { type IStringAnyMap } from "../../../app-utils/type.js";
import { checkboxDummyColumn, getCellRenderer } from "./cell-renderer-factory.js";
import { CheckboxHeaderCell, type CheckboxHeaderCellProps } from "./cells/headers/CheckboxHeaderCell.js";
import { HeaderCell, type HeaderCellProps } from "./cells/headers/HeaderCell.js";
import { HeaderGroupCell } from "./cells/headers/HeaderGroupCell.js";
import { defaultValueFormatter, defaultValueGetter, gridColumnTypes } from "./column-props-factory.js";
import { type IGridContextProps } from "./InMemoryTableWithSelectionModel.js";

export interface ICustomColDef extends ColDef {
  // TODO use cellRendererParams for these custom params [see SEL6-2042] (but not if these are needed in the factory?)
  sortable?: boolean;
  sorting?: SortData; // TODO use the out of the box sorting "sort" and "sortIndex"
  filterable?: boolean;
  filterFramework?: RsqlFilterConstraint;
  filter?: any; // should be RsqlFilterConstraint, use filterFramework
  hidden?: boolean;
  rowGroup?: boolean;
  attribute?: Attribute; // sometimes needed within the cell renderer to infer some styling or formatting
  openLinkExternal?: boolean;
  groupField?: string;
  multiValuesContext?: string;
  cellExpansion?: string; // see CELL_EXPANSION
  headerComponentParams?: HeaderCellProps;
  searchAttributes?: SearchAttribute; // sometimes the search attributes contain useful information for the cell display e.g. the list of mutant genomes for the "profile" cell
}

const defaultColGroupDef = {
  children: [],
  headerGroupComponent: HeaderGroupCell,
  headerGroupComponentParams: {},
};

export type IColumnDef = ICustomColDef | ColGroupDef;

export interface IDataTableProps {
  tableData: ITableData;
  hideCheckboxes?: boolean;
  noRowsText?: string;
  gridContext?: IGridContextProps;
  wrapColumnHeaders: boolean;

  onRowSelection: (rowId: number, rowIndex: number, selected: boolean) => void;
  onRangeSelection: (rowIndex: number) => void;
  onAllSelected: (selected: boolean) => void;
  onColumnSortClick: (columnField: string, sortOrder: string, multiSort: boolean) => void;
  onRemoveFilterClick: (columnField: string) => void;
  onMenuButtonClicked: (columnMenuData: any) => void;
  loadTableRows: (firstRowIndex: number, lastRowIndex: number) => Promise<any>;
  refreshTableColumnsMetadata: () => Promise<any>;
  onColumnDragStopped: (orderedColumns: ITableColumn[]) => void;
  onFeatureFocused?: (rowData: any) => void;
  fetchPosition: (targetId: number, tableSpec: InputTableQuerySpec) => Promise<number>;
  cellRendererParamsLookup?: IStringAnyMap; // in order to provide to the cell some additional data
  onGridSizeChanged?: (event: GridSizeChangedEvent) => void;
  openLinkExternal?: boolean;
  ensureRowIdIsVisible?: (rowId: number, rowIndex: number) => void;
  onCellMouseOver: (rowId: number, tableQuerySpec: InputTableQuerySpec) => void;
  onCellMouseOut: (rowId: number, tableQuerySpec: InputTableQuerySpec) => void;
}

class DataSource implements IServerSideDatasource {
  // Reference to the row loading method
  private loadRows: (firstRowIndex: number, lastRowIndex: number) => Promise<any>;
  // react on the response, using the gridApi imperatively e.g. for showing eventually 'no rows' message
  private afterDataLoadHook: any;

  // since we currently support only one single request at a time, we should cancel it before it succeeds in order to trigger immediately the next one (e.g. filtering changed)
  private currentParams: IServerSideGetRowsParams = null;

  cancelCurrentQuery = () => {
    // we make sure the precedent query is over in order to let ag-grid trigger the new query immediately (otherwise it is queued and might cause side effects e.g. the rows ids are reset to trigger fetching count data but the first query might overwrite the row ids , and therefore the new request will not fetch the counts/row ids metadata, leading to issues like in SEL8-1815 (data not aligned to counts)
    this.currentParams?.fail();
  };

  constructor(loadRows: any, afterDataLoadHook: any, setCancelCurrentQuery: any) {
    this.loadRows = loadRows;
    this.afterDataLoadHook = afterDataLoadHook;
    setCancelCurrentQuery(this.cancelCurrentQuery);
  }

  // grid calls this to get rows
  getRows = (params: IServerSideGetRowsParams): void => {
    params.api.setGridOption("loading", true);
    this.currentParams = params;
    // invoke your server with request params supplied by grid
    this.loadRows(params.request.startRow, params.request.endRow)
      .then(
        (response: Table) => {
          this.currentParams = null;
          if (response) {
            // since some old requests might be ignored
            // call the success callback
            params.success({
              rowData: response.pageRows,
              rowCount: Math.min(MAX_ROW_COUNT, response.filteredRowCount),
            });
            params.api.setGridOption("loading", false);
          } else {
            params.fail(); // probably an "ignore" case
          }
          return response;
        },
        (_failure) => {
          params.api.setGridOption("loading", false);
          // inform the grid the request failed
          params.fail();
        },
      )
      .then(this.afterDataLoadHook);
  };

  // optional destroy method, if your datasource has state it needs to clean up
  destroy() {
    // Nothing to do here for now ?
  }
}

interface IDataTableState {
  defaultColDefs: ColDef<IIdentifiable>;
}

export default class DataTable extends PureComponent<IDataTableProps, IDataTableState> {
  public static RESIZE_DEBOUNCE_TIMER = 50;

  private gridApi: GridApi;

  private dataSource: IServerSideDatasource;
  private cancelCurrentQuery: () => void;

  private checkboxHeaderCell: CheckboxHeaderCell;
  private readonly selectionModel: ISelectionModel;
  private columnWasMoved: boolean;
  private clientWidth: number; // to make sure we trigger some refresh only if client width did change
  private componentIsUnmouting: boolean; // protection against refresh while component is unmounting, since we use a setTimeout to refresh... see  [SEL5-1218, SEL5-1138]

  constructor(props: IDataTableProps) {
    super(props);
    // Debounce the onGridSizeChanged handler to prevent flickering of columns when resizing the window width
    this.onGridSizeChanged = debounce(this.onGridSizeChanged, DataTable.RESIZE_DEBOUNCE_TIMER);

    // init private state
    this.selectionModel = cloneSelectionModel(emptySelectionModel);
    this.columnWasMoved = false;
    this.componentIsUnmouting = false;

    this.state = {
      defaultColDefs: {},
    };
  }

  componentDidMount() {
    // headerComponentParams should have a stable reference, otherwise the grid columns do resize each time this ref changes ! (happened after update to ag-grid v28.0.2 / ag-grid-react v28.0.0)
    this.setState({ defaultColDefs: this.getDefaultColDefs() });
  }

  componentDidUpdate(prevProps: IDataTableProps) {
    const tableColumnsChanged: boolean = this.props.tableData.columns !== prevProps.tableData.columns;

    // if restrictions did change (except ref genome, still part of the datatablecontainer key) ;
    // be aware that the change of rowRestriction would trigger a component unmount/mount, therefore we focus on the
    // modifications within the same restriction
    const tableRestrictionsChanged = !sameTableQuerySpecRestrictions(
      this.props.tableData.tableQuerySpec.restrictions,
      prevProps.tableData.tableQuerySpec.restrictions,
    );
    const tableReloadIsNecessary: boolean = !!prevProps.tableData.rowIds && !this.props.tableData.rowIds; // meaning that we want to trigger the reload, perhaps some rows have been added or deleted
    const expansionChanged = symmetricDifference(
      getActiveExpansionPath(this.props.tableData),
      getActiveExpansionPath(prevProps.tableData),
    ).length;

    if (tableColumnsChanged || tableReloadIsNecessary || expansionChanged || tableRestrictionsChanged) {
      if (
        getFilterExpression(this.props.tableData.columns) !== getFilterExpression(prevProps.tableData.columns) ||
        getSortOrderExpressionFromTableColumns(this.props.tableData.columns) !==
          getSortOrderExpressionFromTableColumns(prevProps.tableData.columns) ||
        // https://www.ag-grid.com/javascript-data-grid/server-side-model-row-height/ states :
        // For Server-Side Row Model the row height cannot be changed once it is set.
        // ... this is probably why resetRowHeights does not seem to work and therefore we have to purge the data again
        expansionChanged // [additional older comment: looks like there is a bug with dynamic row height and cache purging and keeping scroll position, we need to scroll to top to avoid an empty grid in some cases after expanding/scrolling/collapsing]
      ) {
        // scroll to top should happen before the full reload
        if (this.gridApi) {
          this.gridApi.ensureIndexVisible(0);
          this.cancelCurrentQuery();
        }
      }

      // purge the cached data and maintains the scroll position
      this.gridApi?.refreshServerSide({ purge: true });
    }

    if (tableColumnsChanged || this.props.wrapColumnHeaders !== prevProps.wrapColumnHeaders) {
      this.updateColumnDefs(); // does not keep the column width ! =>
      // seems like https://www.ag-grid.com/documentation/react/fine-tuning/ is giving some clues about columnDefsChangeDetectionStrategy option, but this seems to have disappeared from the grid options ?!
    }

    if (
      this.props.tableData.selectionModel !== prevProps.tableData.selectionModel ||
      this.props.tableData.filteredRowCount !== prevProps.tableData.filteredRowCount
    ) {
      // selection model can be changed from the outside, we need to sync with the ag-grid selection
      this.updateSelectionModel();
    }

    // single row update e.g. in case of status change notification
    if (this.props.tableData?.rowToUpdate !== prevProps.tableData?.rowToUpdate) {
      const rowToUpdate = this.props.tableData?.rowToUpdate;
      this.gridApi?.forEachNode((rowNode) => {
        if (rowToUpdate && rowToUpdate?.id === rowNode.data?.id) {
          // directly update data in rowNode
          rowNode.setData({ ...rowNode.data, ...rowToUpdate });
        }
      });
    }

    this.checkScrollToEntity();
  }

  componentWillUnmount() {
    this.componentIsUnmouting = true;
  }

  updateColumnDefs = () => {
    const { tableData } = this.props;

    if (this.gridApi) {
      const columnGroups: IColumnDef[] = createColumnGroups({
        tableColumns: tableData.columns,
        tableQuerySpec: tableData.tableQuerySpec,
        createCheckboxColumnDef: !this.props.hideCheckboxes ? this.createCheckboxColumnDef : undefined,
        cellRendererParamsLookup: this.props.cellRendererParamsLookup,
        openLinkExternal: this.props.openLinkExternal,
        wrapColumnHeaders: this.props.wrapColumnHeaders,
      });

      this.gridApi.setGridOption("columnDefs", columnGroups);
      this.sizeColumnsToFit();
    }
  };

  checkScrollToEntity = () => {
    if (this.gridApi && this.gridApi.getDisplayedRowCount() > 1) {
      if (this.props.tableData.scrollToRowId) {
        // we do not compare to previous 'scrollToRowId' because the scrolling might need to happen at a later stage, when the table in loaded
        this.ensureEntityIsVisible(this.props.tableData.scrollToRowId, this.props.tableData);
        this.props.ensureRowIdIsVisible(null, null); // reset the 'scrollToRowId' of redux module so that we don't scroll to it again at next update
      } else if (this.props.tableData.scrollToRowIndex) {
        // we do not compare to previous 'scrollToRowIndex' because the scrolling might need to happen at a later stage, when the table in loaded
        this.gridApi.ensureIndexVisible(this.props.tableData.scrollToRowIndex, null);
        this.props.ensureRowIdIsVisible(null, null); // reset the 'scrollToRowId' of redux module so that we don't scroll to it again at next update
      }
    }
  };

  ensureEntityIsVisible = (targetId: number, tableData: ITableData) => {
    if (targetId === -1) {
      // Because Java's Objects.hash returns an Int that can be negative in case of wrapping.
      // For backward compatibility we consider -1 to be a undefined value and can't consider it valid.
      return;
    }
    if (tableData.rowIds) {
      const targetIndex: number = tableData.rowIds.findIndex((id: number) => id === targetId);
      if (targetIndex > -1) {
        this.gridApi?.ensureIndexVisible(targetIndex, "middle");
        // this.flashRow(targetIndex);
      } else {
        // The entity id could not be found in the list of rowIds, might be because it is located beyond the threshold in the rowIds table
        this.props.fetchPosition(targetId, tableData.tableQuerySpec).then((index: number) => {
          if (index > -1) {
            // if -1 that probably means it does not match the currently set filter
            this.gridApi?.ensureIndexVisible(index, "middle");
            // this.flashRow(index);
          }
        });
      }
    }
  };

  getRowHeight = (params: RowHeightParams) => {
    return calculateRowHeightForExpandedColumns(this.props.tableData, params);
  };

  onFeatureFocused = (event) => {
    // prevent the feature focus if the target is actually the selection checkbox
    if (
      this.props.onFeatureFocused &&
      !event.event.target.htmlFor?.startsWith("checkbox") &&
      !event.event.target.id.startsWith("checkbox")
    ) {
      this.props.onFeatureFocused(event.data);
    }
  };

  // overlays are not automatically shown, why not?? https://www.ag-grid.com/documentation/react/overlays/
  afterDataLoadHook = (response: Table) => {
    if (response) {
      this.checkScrollToEntity();
      // Show or hide 'No rows' overlay
      if (response.filteredRowCount === 0) {
        // show 'no rows' overlay
        this.gridApi?.showNoRowsOverlay();
      } else {
        this.gridApi?.setGridOption("loading", false);
        // existing selection can apply to new nodes/rows, we need to sync with the ag-grid selection
        this.updateSelectionModel();
      }
    }
  };

  setCancelCurrentQuery = (cancelQuery) => {
    this.cancelCurrentQuery = cancelQuery;
  };

  onGridReady = (params: GridReadyEvent) => {
    // the following fixes a 'invalid parentComponent' warning in the console for table to table transitions
    if (this.componentIsUnmouting) {
      return;
    }
    this.gridApi = params.api;

    // enable infinite scrolling with server side row model
    this.dataSource = new DataSource(this.props.loadTableRows, this.afterDataLoadHook, this.setCancelCurrentQuery);
    this.gridApi.setGridOption("serverSideDatasource", this.dataSource);

    this.updateColumnDefs();
  };

  debouncedSizeColumnsToFit = debounce(() => {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }, 200);

  onGridSizeChanged = (params: GridSizeChangedEvent) => {
    if (this.gridApi && this.props.tableData.columns?.length > 0) {
      if (!this.clientWidth || params.clientWidth !== this.clientWidth) {
        this.clientWidth = params.clientWidth;
        this.debouncedSizeColumnsToFit();
      }
      if (this.props.onGridSizeChanged) {
        this.props.onGridSizeChanged(params);
      }
    }
  };

  cleanupRanges = (rangeSelectionFinished: boolean, ranges: CellRange[]): void => {
    // Ranges are cleaned up:
    // - if there is more than one range selected (using ctrl-drag)
    // - if the range includes the checkbox column
    //
    // Only the last selected range will be kept, and checkbox columns are excluded from the range.
    //
    // we need to adjust the ranges if there is more than one (drop all except last)
    let rangesNeedAdjustment = false;
    const newRanges: CellRangeParams[] = [];

    // process only the last range
    const range = ranges[ranges.length - 1];
    // we will drop it if it only contains checkboxes
    let dropRange = false;
    // prepare new range settings by applying the original ones
    const rowStart: number = range.startRow.rowIndex;
    const rowEnd: number = range.endRow.rowIndex;
    let columnStart: Column = range.startColumn;
    let columnEnd: Column = range.columns[range.columns.length - 1];

    // when dragging finished, remove checkbox cols and previous selections
    if (rangeSelectionFinished) {
      if (ranges.length > 1) {
        rangesNeedAdjustment = true;
      }
      // we need to check both the start and the end of the range for checkbox cells
      if (columnStart.getColId() === CHECKBOX_COLUMN) {
        // If the first col is the checkbox, replace it if a second col is available.
        // Otherwise drop it.
        rangesNeedAdjustment = true;
        if (range.columns[1]) {
          columnStart = range.columns[1];
        } else {
          dropRange = true;
        }
      } else if (columnEnd.getColId() === CHECKBOX_COLUMN) {
        // If the last col is the checkbox, replace it if a second col is available.
        // Otherwise drop it.
        rangesNeedAdjustment = true;
        if (range.columns.length > 1) {
          // note that the "columns" list of the range object always contains the cols from left to right!
          columnEnd = range.columns[1];
        } else {
          dropRange = true;
        }
      }
    }

    // if we keep the range, build a new range object and keep it
    if (!dropRange) {
      const newRange: CellRangeParams = {
        rowStartIndex: rowStart,
        rowEndIndex: rowEnd,
        columnStart: columnStart,
        columnEnd: columnEnd,
      };
      newRanges.push(newRange);
    }

    // completely replace the ranges if we modified one (or dropped it)
    if (rangesNeedAdjustment) {
      this.gridApi.clearCellSelection();
      // note that currently there will be only one range in the list...
      for (const currRange of newRanges) {
        this.gridApi.addCellRange(currRange);
      }
    }
  };

  onRangeSelectionChanged = (params: RangeSelectionChangedEvent) => {
    // execute only when the mouse button is released
    if (this.gridApi) {
      // get the current ranges from ag-grid
      const ranges: CellRange[] = this.gridApi.getCellRanges();
      if (ranges && ranges.length) {
        this.cleanupRanges(params.finished, ranges);
      }
    }
  };

  // Make sure that only useful information is copied to the clipboard
  // TODO: This section may contain much more useful logic for certain content.
  processCellForClipboard = (params) => {
    if (params.column.colDef.cellType === CELL_TYPE.DATE) {
      // date field
      return formatDateTimeFromISO(params.value, { hideTime: true });
    }
    if (params.column.colDef.cellType === CELL_TYPE.DATE_TIME) {
      // date field
      return formatDateTimeFromISO(params.value);
    } else if (params.value instanceof Array) {
      // standard list cell contents
      return params.value.join(", ");
    } else {
      // contents should be fine
      return params.value;
    }
  };

  onColumnMoved = (params: ColumnMovedEvent) => {
    const fixedColCount = this.props.hideCheckboxes ? 1 : 2;
    preventColumnPinning(params, fixedColCount);
    this.columnWasMoved = true;
  };

  onColumnDragStopped = (params: DragStoppedEvent) => {
    const offset = this.props.hideCheckboxes ? 0 : 1;

    // Fix issue where onColumnDragStopped handler is called even if no columns where moved
    if (this.columnWasMoved) {
      // use gridApi.getAllGridColumns() ?
      const columnState: ColumnState[] = params.api.getColumnState();
      let newCols: ITableColumn[] = [];
      // first push all new ordered columns according to the provided state
      for (let pos = offset; pos < columnState.length; pos++) {
        // ignoring the 'checkboxColumn' at 0
        const col: ITableColumn | undefined = this.props.tableData.columns.find(
          (oldCol: ITableColumn) => oldCol.field === columnState[pos].colId,
        );
        if (col) {
          newCols.push(col);
        }
      }
      // then concat with all remaining items
      newCols = newCols.concat(
        this.props.tableData.columns.filter((oldCol: ITableColumn) => {
          for (let pos = offset; pos < columnState.length; pos++) {
            if (oldCol.field === columnState[pos].colId) {
              return false;
            }
          }
          return true;
        }),
      );

      this.columnWasMoved = false;

      this.props.onColumnDragStopped(newCols);
    }
  };

  getHeaderCellProps = (): HeaderCellProps => {
    return {
      onColumnSortClick: this.props.onColumnSortClick,
      onRemoveFilterClick: this.props.onRemoveFilterClick,
      onMenuButtonClicked: this.props.onMenuButtonClicked,
      hideMenu: false,
      getGridColumns: this.getGridColumns,
    };
  };

  getDefaultColDefs = (): ColDef<IIdentifiable> => {
    const headerComponentParams: HeaderCellProps = {
      onColumnSortClick: this.props.onColumnSortClick,
      onRemoveFilterClick: this.props.onRemoveFilterClick,
      onMenuButtonClicked: this.props.onMenuButtonClicked,
      hideMenu: false,
      getGridColumns: this.getGridColumns,
    };

    return {
      minWidth: DEFAULT_MIN_COLUMN_WIDTH,
      valueGetter: defaultValueGetter,
      valueFormatter: defaultValueFormatter, // by default the formatted value of an array is a joined string ; since the generic TableCellText takes into account the formattedValue we rather need the raw data
      resizable: true,
      headerComponent: HeaderCell,
      headerComponentParams: headerComponentParams,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    };
  };

  private getGridColumns = (): ITableColumn[] => {
    // usefull e.g. in the HeaderCell, passed via the ag.grid context
    return this.props.tableData.columns;
  };

  private onRowMouseOverTimer: number;

  private onCellMouseOver = (evt) => {
    clearTimeout(this.onRowMouseOverTimer);
    return this.props.onCellMouseOver(evt.data.id, this.props.tableData.tableQuerySpec);
  };

  private onCellMouseOut = (evt) => {
    this.onRowMouseOverTimer = window.setTimeout(() => {
      this.props.onCellMouseOut(evt.data.id, this.props.tableData.tableQuerySpec);
    }, 500);
  };

  sizeColumnsToFit = () => {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  };

  render(): ReactElement {
    const { tableData } = this.props;

    return (
      <div
        className="ag-theme-selector-grid fill data-table-grid"
        id={`${createUniqueKeyFromTableSpec(tableData.tableQuerySpec)}`}
        style={{ maxHeight: "100%" }}
      >
        {/* the above ID is required to identify the table and retrieve the header cells for dynamic header height calculation */}
        <AgGridReact
          context={this.props.gridContext}
          rowModelType="serverSide"
          //blockLoadDebounceMillis={100} // loading of blocks is delayed, allows for skipping over blocks when scrolling to advanced positions
          cacheBlockSize={100} // How many rows for each block in the cache.
          cacheOverflowSize={10} // how many rows beyond the current last row the scrolls should allow to scroll
          maxConcurrentDatasourceRequests={1} // How many requests to hit the server with concurrently WARNING if > 1 then the table-data module needs some adaptation, it currently ignores previous requests
          //maxBlocksInCache: null If using Infinite Scroll, setting maxBlocksInCache will stop dynamic row heights from working. This is a restriction in the design. If you are using dynamic row height, ensure maxBlocksInCache is not set.
          // columnDefsChangeDetectionStrategy={ChangeDetectionStrategyType.DeepValueCheck}
          onRowClicked={this.onFeatureFocused}
          onFirstDataRendered={this.sizeColumnsToFit}
          onGridReady={this.onGridReady}
          onGridSizeChanged={this.onGridSizeChanged}
          onColumnMoved={this.onColumnMoved}
          onDragStopped={this.onColumnDragStopped}
          getRowHeight={this.getRowHeight}
          onSelectionChanged={this.onRangeSelectionChanged}
          processCellForClipboard={this.processCellForClipboard}
          onCellMouseOver={this.props.onCellMouseOver ? this.onCellMouseOver : undefined}
          onCellMouseOut={this.props.onCellMouseOut ? this.onCellMouseOut : undefined}
          defaultColDef={this.state.defaultColDefs}
          defaultColGroupDef={defaultColGroupDef}
          columnTypes={gridColumnTypes}
          rowSelection={{
            mode: "multiRow",
            checkboxes: false,
            headerCheckbox: false,
            enableClickSelection: false,
          }}
          cellSelection={{
            handle: {
              mode: "range",
            },
            suppressMultiRanges: true,
          }}
          suppressContextMenu={true}
          suppressDragLeaveHidesColumns={true}
          headerHeight={HEADER_GROUP_HEIGHT}
          groupHeaderHeight={HEADER_GROUP_HEIGHT}
          rowHeight={ROW_HEIGHT} // Needed for ensureIndexVisible
          overlayNoRowsTemplate={`<span class="grid-overlay-no-rows">${
            this.props.noRowsText ? this.props.noRowsText : "No rows to show"
          }</span>`}
          getRowId={(params: GetRowIdParams<IIdentifiable>) => params.data.id as string}
          animateRows={false}
          overlayLoadingTemplate={'<span class="grid-overlay-no-rows">Loading data...</span>'}
          loadingCellRenderer={LoadingRow}
          suppressAnimationFrame={true} // prevent the table display to flicker when scrolling and redrawing rows
          enableBrowserTooltips={true}
          theme={themeSelector}
        />
      </div>
    );
  }

  // we cannot use the build in selection model because server-side row model does not support 1) the select all 2) the range selection across row blocks
  updateSelectionModel = () => {
    const tableData = this.props.tableData;
    const newSelectionModel: ISelectionModel = tableData.selectionModel;

    this.selectionModel.selectedIds = newSelectionModel.selectedIds.slice(0);
    this.selectionModel.lastIndexOperation = newSelectionModel.lastIndexOperation;
    this.selectionModel.lastOperationIsSelection = newSelectionModel.lastOperationIsSelection;

    // Update ag-grid selection model because this is the only way to set .ag-row-selected for selected table rows
    if (this.gridApi) {
      const nodesToSelect: IRowNode[] = [];
      const nodesToDeselect: IRowNode[] = [];
      this.gridApi.forEachNode((node: IRowNode) => {
        if (node.data) {
          const selected = isSelected(newSelectionModel, node.data.id);
          if (selected && !node.isSelected()) {
            nodesToSelect.push(node);
          } else if (!selected && node.isSelected()) {
            nodesToDeselect.push(node);
          }
        }
      });
      this.gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
      this.gridApi.setNodesSelected({ nodes: nodesToDeselect, newValue: false });

      // need to redraw the rows to see the updated selection
      this.gridApi.refreshCells({ force: true }); // seems to be required to draw the checkboxes
    }

    // Set toggle checkbox
    if (this.checkboxHeaderCell) {
      const length = newSelectionModel.selectedIds.length;
      this.checkboxHeaderCell.setSelected(length !== 0 && length >= tableData.filteredRowCount);
    }
  };

  createCheckboxColumnDef = (): IColumnDef => {
    const tableData = this.props.tableData;

    const checkboxCellRenderer = getCellRenderer(checkboxDummyColumn);

    const headerCellProps: CheckboxHeaderCellProps = {
      onChange: this.props.onAllSelected,
      defaultChecked: false,
      uniqueGridIdentifier: createUniqueKeyFromTableSpec(tableData.tableQuerySpec), // important to have it unique because of our way to register the reference
      setCheckboxRef: this.registerCheckboxHeaderCellReference,
    };

    return {
      ...gridColumnTypes[CELL_TYPE.CHECKBOX],
      cellRenderer: checkboxCellRenderer,
      cellRendererParams: {
        onChange: this.handleRowSelectionChange,
      },
      /** The custom header component to be used for rendering the component header in the hosting framework (ie: React/Angular). If none specified the default ag-Grid is used**/
      headerComponent: CheckboxHeaderCell,
      /** The custom header component parameters**/
      headerComponentParams: headerCellProps,
    };
  };

  registerCheckboxHeaderCellReference = (component: CheckboxHeaderCell) => {
    this.checkboxHeaderCell = component;
    this.checkboxHeaderCell.setSelected(
      this.selectionModel.selectedIds.length > 0 &&
        this.selectionModel.selectedIds.length >= this.props.tableData.filteredRowCount,
    );
  };

  handleRowSelectionChange = (rowIndex: number, rowId: number, selected: boolean, rangeCheck: boolean) => {
    // Trigger Redux actions
    if (rangeCheck) {
      this.props.onRangeSelection(rowIndex);
    } else {
      // Set selection but don't clear selection state of other nodes
      const node = this.gridApi.getDisplayedRowAtIndex(rowIndex);
      node?.setSelected(selected, false);
      setSelected(this.selectionModel, rowId, rowIndex, selected);
      this.props.onRowSelection(rowId, rowIndex, selected);
    }

    // Set toggle checkbox
    if (this.checkboxHeaderCell) {
      this.checkboxHeaderCell.setSelected(
        this.selectionModel.selectedIds.length >= this.props.tableData.filteredRowCount,
      );
    }
  };
}

function LoadingRow(): JSX.Element {
  return (
    <>
      <LoadingSpinner size={16} style={{ margin: "0 20px" }} />
      Loading...
    </>
  );
}
